<template>
  <esg-base-dashboard-card :title="`${productFamily.name} 제품군`">
    <template #actions>
      <b-form>
        <b-form-row>
          <b-col>
            <label class="label"
                   for="sortOrder">정렬</label>
            <b-form-select id="sortOrder"
                           size="sm"
                           v-model="sort">
              <option value="asc">오름차순</option>
              <option value="dsc">내림차순</option>
            </b-form-select>
          </b-col>
          <b-col>
            <label class="label"
                   for="year">연도</label>
            <b-form-select id="year"
                           size="sm"
                           v-model="year">
              <option v-for="year in yearOptions"
                      :key="year"
                      :value="year">{{ year }}
              </option>
            </b-form-select>
          </b-col>
        </b-form-row>
      </b-form>
    </template>
    <esg-base-chart height="300px">
      <loading :active="loading"
               :z-index="1000"
               :is-full-page="false"/>
        <apex-chart ref="chart"
                    type="bar"
                    height="300"
                    v-show="!loading"
                    :options="chartOptions"
                    :series="series"/>
    </esg-base-chart>
    <esg-table-without-meta :items="items"
                            :fields="fields"
                            v-show="!loading"
                            size="sm"/>
  </esg-base-dashboard-card>
</template>
<script>
import EsgBaseDashboardCard from "@/components/esg/base/EsgBaseDashboardCard.vue";
import EsgBaseChart from "@/components/esg/base/EsgBaseChart.vue";
import EsgTableWithoutMeta from "@/components/esg/base/EsgTableWithoutMeta.vue";

export default {
  components: { EsgTableWithoutMeta, EsgBaseChart, EsgBaseDashboardCard },
  props: {
    product: {
      type: Object
    },
    productFamily: {
      type: Object
    },
  },
  watch: {
    productFamily: {
      immediate: true,
      deep: true,
      async handler(productFamily) {
        if (!productFamily || !productFamily.code || !productFamily.name) return
        await this.initialize();
        await this.fetchData();
      }
    },
    sort: {
      handler() {
        this.setSeries();
        this.setTable();
        this.refreshChart();
      }
    },
    year: {
      handler() {
        this.fetchData();
      }
    }
  },
  data() {
    return {
      loading: false,
      list: [],
      items: [],
      fields: [
        { name: 'name', label: '제품명', list: true, type: 'String' },
        { name: 'total', label: '생애주기 온실가스 총배출량 (tCO2)', list: true, type: 'Number' }
      ],
      series: [],
      products: [],
      sort: 'dsc',
      year: null,
      yearOptions: [],
      chartOptions: {
        chart: {
          type: 'bar',
          stacked: true,
          toolbar: { show: false },
          zoom: { enabled: false },
          events: {
            dataPointSelection: (event, chartContext, config) => {
              let product = config.w.config.xaxis.categories[config.dataPointIndex]
              this.$emit('productSelected', product)
            },
            dataPointMouseEnter: (event) => {
              event.target.style.cursor = "pointer"
            },
          }
        },
        plotOptions: {
          bar: {
            dataLabels: {
              total: {
                enabled: true,
                formatter: (val) => this.formatNumber(val, 0)
              }
            }
          },
        },
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          categories: [],
          labels: {
            formatter: product => product.name
          },
          crosshairs: { show: false }
        },
        yaxis: {
          show: true,
          title: {
            text: '탄소 배출량 (tCO2)',
            style: {
              fontSize: '12px',
              fontWeight: 'normal',
            },
          },
          labels: {
            formatter: val => this.compactFormatNumber(val)
          }
        },
        legend: {
          position: 'right',
        },
        tooltip: {
          y: {
            formatter: (val, data) => {
              let dataPointIndex = data.dataPointIndex;
              let total = data.series.map(s => s[dataPointIndex]).reduce((a, b) => a + b, 0);
              let share = 100 * val / total
              return `${this.formatNumber(val, 2)} tCO2 (${this.formatNumber(share)}%)`
            }
          }
        },
        grid: { show: false },
        responsive: [
          {
            breakpoint: 600,
            options: {
              legend: {
                position: 'bottom'
              }
            }
          }
        ]
      },
    }
  },
  methods: {
    async initialize() {
      // Purpose: Fetch the whole data and figure out what years are available
      if (!this.productFamily.code || !this.productFamily.name) return

      try {
        this.loading = true

        let args = [{
          productFamilies: [this.productFamily.code],
        }];

        let data = new FormData();
        data.append('path', 'esg/lca/ghg_emission/main');
        data.append('json', JSON.stringify(args));

        this.abortController = new AbortController();
        let response = await axios.post('esg/lca/run/python/chart-data', data, {
          headers: { Authorization: accessToken() },
          signal: this.abortController.signal
        });

        // If there is no data, return
        if (!response.data.data) {
          this.$emit('noData')
          return;
        }

        // Parse JSON data
        let list = JSON.parse(response.data.data)[0].data;

        // Set year options
        this.yearOptions = [...new Set(list.map(row => row.year))];
        this.yearOptions.reverse() // descending order

        // Set the initial year
        this.year = this.yearOptions[0];
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    async fetchData() {
      if (!this.productFamily.code || !this.productFamily.name) return
      if (!this.year) return

      try {
        this.loading = true

        let args = [{
          years: [this.year],
          productFamilies: [this.productFamily.code],
          groupKeys: ['year', 'product_family_code', 'product_code', 'product_name', 'category']
        }];

        let data = new FormData();
        data.append('path', 'esg/lca/ghg_emission/main');
        data.append('json', JSON.stringify(args));

        let response = await axios.post('esg/lca/run/python/chart-data', data, {
          headers: { Authorization: accessToken() }
        });

        // Parse JSON data
        let list = JSON.parse(response.data.data)[0].data;
        this.list = list;

        // Extract product info
        let products = [...new Set(list.map(row => row.product_code))]
            .map(productCode => ({
              productFamilyCode: this.productFamily.code,
              productCode: productCode,
              name: list.find(row => row.product_code === productCode).product_name
            }));

        // Calculate totals for products
        this.setTotal(products);

        // Sort products in the descending order of the totals
        products.sort((a, b) => b.total - a.total);
        this.products = products;

        // Set series
        this.setSeries();

        // Set table data
        this.setTable();
      } catch (error) {
        this.loading = false
        await this.showError(error)
      } finally {
        this.loading = false
        this.refreshChart()
      }
    },
    setTotal(products) {
      products.forEach(product => {
        product.total = this.list.filter(row => row.product_code === product.productCode)
            .reduce((a, b) => a + b.values, 0);
      });
    },
    setSeries() {
      let products = structuredClone(this.products);
      if (this.sort === 'asc') {
        products.reverse();
      }

      // Group data based on LCA process and product name
      let series = [];
      let categories = this.$store.getters['ghg/lifeCycleStages'];
      categories.forEach(category => {
        let data = [];
        products.forEach(product => {
          let value = this.list
              .filter(row => row.category === category.value && row.product_name === product.name)
              .map(row => row.values)
              .pop() || 0;
          data.push(value);
        });
        series.push({ name: category.label, data });
      })

      // Set series and x axis labels
      this.series = series;
      this.chartOptions.xaxis.categories = products;
    },
    setTable() {
      let products = structuredClone(this.products);
      if (this.sort === 'asc') {
        products.reverse();
      }
      this.items = products
    }
  }
}
</script>

<style scoped>
.custom-select {
  padding: 0.3rem 2.5rem 0.3rem .8rem;
  height: 2rem;
  border-radius: 10px;
  border: 1px solid #E7E7EE;
  background-color: #F6F6FA;
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15.834 7.5L10.0007 13.3333L4.16732 7.5" stroke="%232E2E38" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>');
  background-position: calc(100% - 8px) 5px;
  background-size: 20px 20px;
  background-repeat: no-repeat;
  font-family: 'NanumGothic';
  color: #2E2E38;
  font-size: .875rem;
  font-weight: 400;
}

.custom-select:focus {
  border-color: unset;
  box-shadow: unset;
}
</style>